import { useCallback, useState } from 'react';

const PRINT_STYLE_ELEMENT_ID = 'print-force-desktop';
const PRINT_HIDDEN_CLASS = 'print:hidden';
const SIDEBAR_OFFSET_CLASS_PREFIX = 'desktop:ml-';

type SavedStyle = { el: HTMLElement; cssText: string };

const collectResponsiveCssAsPrint = (): string => {
  let printCss = '@media print {\n';

  for (const sheet of document.styleSheets) {
    try {
      for (const rule of sheet.cssRules) {
        if (!(rule instanceof CSSMediaRule)) continue;
        if (!rule.media.mediaText.match(/min-width:\s*(\d+)/)) continue;
        for (const innerRule of rule.cssRules) {
          printCss += innerRule.cssText + '\n';
        }
      }
    } catch {
      // Cross-origin stylesheet — ignoruj
    }
  }

  return printCss + '}';
};

const injectPrintStyle = (): HTMLStyleElement => {
  const styleEl = document.createElement('style');
  styleEl.id = PRINT_STYLE_ELEMENT_ID;
  styleEl.textContent = collectResponsiveCssAsPrint();
  document.head.appendChild(styleEl);
  return styleEl;
};

const hideSidebarAndOffset = (): SavedStyle[] => {
  const saved: SavedStyle[] = [];

  document.querySelectorAll<HTMLElement>('[class]').forEach((el) => {
    if (typeof el.className !== 'string') return;

    if (el.className.includes(PRINT_HIDDEN_CLASS)) {
      saved.push({ el, cssText: el.style.cssText });
      el.style.setProperty('display', 'none', 'important');
    }

    if (el.className.includes(SIDEBAR_OFFSET_CLASS_PREFIX)) {
      saved.push({ el, cssText: el.style.cssText });
      el.style.setProperty('margin-left', '0', 'important');
      el.style.setProperty('width', '100%', 'important');
    }
  });

  return saved;
};

export const usePrintReport = () => {
  const [isPrinting, setIsPrinting] = useState<boolean>(false);

  const printReport = useCallback(() => {
    setIsPrinting(true);

    const styleEl = injectPrintStyle();
    const savedStyles = hideSidebarAndOffset();

    const cleanup = () => {
      styleEl.remove();
      savedStyles.forEach(({ el, cssText }) => {
        el.style.cssText = cssText;
      });
      setIsPrinting(false);
      window.removeEventListener('afterprint', cleanup);
    };

    window.addEventListener('afterprint', cleanup);
    window.print();
  }, []);

  return { printReport, isPrinting };
};
